<script setup>
import { ref, reactive, onMounted, computed, watch, toRaw } from 'vue'
import axios from 'axios'
import LogoRaw from '@/assets/barbarossa.png'

const isTreffenlokal = new URLSearchParams(window.location.search).has('treffenlokal')
const _shopkasseParam = new URLSearchParams(window.location.search).get('shopkasse')
const _currentUser = (window.CURRENT_USER || '').toLowerCase()
const isShopkasse = !!_shopkasseParam && (_currentUser === '' || _shopkasseParam.toLowerCase() === _currentUser)
const TYP_CHOICES_ALL = { gwcd: 'GWCD', gwfd: 'GWFD', gwef: 'GWEF', gwrra: 'GWRRA', nongwcdfd: 'kein Club Mitglied', tgo: 'Tagesgast ohne Einschreibung' }
const TYP_CHOICES_TGO = { tgo: 'Tagesgast ohne Einschreibung' }
const TYP_CHOICES = computed(() => {
  if (isTreffenlokal && isShopkasse) return TYP_CHOICES_TGO
  if (isTreffenlokal) return TYP_CHOICES_ALL
  const { tgo, ...rest } = TYP_CHOICES_ALL
  return rest
})

// Gruppe 1: Club-Mitgliedschaften (mehrere gleichzeitig möglich)
const CLUB_WERTE = ['gwcd', 'gwfd', 'gwef', 'gwrra']

function onGwcdmitgliedChange(clickedValue) {
  const current = personalie.gwcdmitglied
  if (CLUB_WERTE.includes(clickedValue)) {
    // Club angeklickt → nongwcdfd und tgo entfernen
    personalie.gwcdmitglied = current.filter(v => v !== 'nongwcdfd' && v !== 'tgo')
  } else if (clickedValue === 'nongwcdfd') {
    // kein Club → nur nongwcdfd behalten
    personalie.gwcdmitglied = current.includes('nongwcdfd') ? ['nongwcdfd'] : current.filter(v => v !== 'nongwcdfd')
    if (personalie.gwcdmitglied.includes('nongwcdfd')) {
      personalie.gwcdmitglied = ['nongwcdfd']
    }
  } else if (clickedValue === 'tgo') {
    // Tagesgast → nur tgo behalten
    personalie.gwcdmitglied = current.includes('tgo') ? ['tgo'] : current.filter(v => v !== 'tgo')
    if (personalie.gwcdmitglied.includes('tgo')) {
      personalie.gwcdmitglied = ['tgo']
    }
  }
  // Sicherstellen: leeres Array nicht zulassen
  if (personalie.gwcdmitglied.length === 0) {
    personalie.gwcdmitglied = ['nongwcdfd']
  }
}

const TYP_OPTION = { 
  F0: 'Verlängerung ohne Frühstück', 
  F1: 'Verlängerung mit Frühstück', 
  F2: 'Verlängerung mit Halbpension',
  F3: 'Verlängerung mit Vollpension',
  B1: 'Buffet Pauschal einmalig',
  B2: 'Buffet Pauschal pro Nacht',
  P1: 'Person Pauschal',
  S0: 'Strom aus med. Gründen',
  E1: 'Fahrer',
  E2: 'Mitreisende',
  E3: 'Kind < 14 Jahre',
  T0: 'T-Shirt kostenfrei',
  N: 'keine Option' 
}
const TYP_ABRECHNUNG = { 
  B: 'BARBAROSSA',
  H: 'HOTEL',
  V: 'VORORT'
}

const TYP_PAUSCHAL = {
    N: '---',
    B: 'Pauschalartikel Buffet',
    P: 'Pauschalartikel Person',
}

const TYP_BIKE = {
    BIKE: 'BIKEMODELL auswählen',
    GL1: 'GL 1000 (1974-1977)',
    GL2: 'GL 1000 (1978-1979)',
    SC02: 'GL 1100 (1980-1983)',
    SC14: 'GL 1200 DX (1984-1988)',
    SC22: 'GL 1500 (1988-2001)',
    SC47: 'GL 1800 (2001-2012)',
    SC68: 'GL 1800 (2012-2017)',
    SC79: 'GL 1800 (ab 2018)',
    F6B: 'F6B (2013-2017)',
    F6C: 'F6C (2013-2017)',
    NNN: 'kein Goldwing Motorrad, andere Marke und Typ',
}

const TYP_BIKE_VARIANT = {
    v: 'Variante auswählen',
    s: 'Solo',
    c: 'Sidecar',
    g: 'Gespann',
    t: 'Trike',
}

const TYP_TSHIRT_SIZE = {
    '': 'für kostenfreies T-Shirt Größe auswählen',
    s: 'T-Shirt S',
    m: 'T-Shirt M',
    l: 'T-Shirt L',
    xl: 'T-Shirt XL',
    xxl: 'T-Shirt 2XL',
    xxxl: 'T-Shirt 3XL',
    xxxxl: 'T-Shirt 4XL',
    xxxxxl: 'T-Shirt 5XL'
}

const treffenLogo = import.meta.env.MODE === 'development'
  ? LogoRaw
  : '/barb' + LogoRaw

const API_BASE = import.meta.env.MODE === 'development'
  ? '/api'             // oder dein Dev-Backend
  : '/barb/api';       // oder dein Prod-Pfad (z.B. mit Subfolder)
const csrfToken = document.getElementById('app')?.dataset.token || ''

const dateRegex = /^([0-2]\d|3[01])\.(0\d|1[0-2])\.(\d{4})$/

const isBarbarossaWinger = (stammtisch) => /^barbarossa[\s\-_]winger$/i.test((stammtisch || '').trim())

const personalie = reactive(
  import.meta.env.MODE === 'development' 
    ? {
        gwcdmitglied: ['nongwcdfd'], // <-- Vorauswahl (Array für Mehrfachauswahl)
        mitgliedsnummer: '',
        vorname: '',
        nachname: '',
        geburtsdatum: '', // <--- hinzufügen
        strasse: '',
        plz: '',
        wohnort: '',
        telefon: '',
        email: '',
        stammtisch: '',
        bemerkung: '',
        uebernachtung: '',
        anreisetag: '',
        abreisetag: '',
        notfall: '',
        nottelefon: '',
        kennzeichen: '',
        bikemodel: 'BIKE',
        bikevariant: 'v',
        anreisekm: '',
        tshirt_size: '',
      }
    : {
        gwcdmitglied: ['nongwcdfd'], // <-- Vorauswahl (Array für Mehrfachauswahl)
        mitgliedsnummer: '',
        vorname: '',
        nachname: '',
        geburtsdatum: '',
        strasse: '',
        plz: '',
        wohnort: '', 
        telefon: '',
        email: '',
        bemerkung: '',
        stammtisch: '',
        uebernachtung: '',
        anreisetag: '',
        abreisetag: '',
        notfall: '',
        nottelefon: '',
        kennzeichen: '',
        bikemodel: 'BIKE',
        bikevariant: 'v',
        anreisekm: '',
        tshirt_size: '',
      }
)

const mitreisende = ref([
  { 
    vorname: '', 
    nachname: '', 
    mitgliedsnummer: '',
    geburtsdatum: '',
    tshirt_size: '',
}
])

const neuerMitreisender = reactive(
  import.meta.env.MODE === 'development' ? {
    vorname: '',
    nachname: '',
    mitgliedsnummer: '',
    geburtsdatum: '',
    tshirt_size: '',
  } : {
    vorname: '',
    nachname: '',
    mitgliedsnummer: '',
    geburtsdatum: '',
    tshirt_size: '',
  }
)

const bestellung = ref([
  { artikel: '', anzahl: 1, option: 'N', option_anzahl: 0, option_text: '', buffet: 'N', buffet_anzahl: 0, buffet_text: '' }
])

const aktTreffen = ref([])
const rechtstexte = ref([])
const alleRechtstexte = ref([])
const alleInfotexte = ref([]) 
const alleArtikelGruppen = ref([])
const tshirtArtikelGruppe = ref([])
const uebernachtungArtikelGruppen = ref([]) 
const ausgewaehlteArtikelgruppe = ref('')
const pauschalArtikelgruppe = ref('')
const treffen = ref(window.TREFFEN_UUID || '')
const anmeldung_geschlossen = ref(window.ANMELDUNG_GESCHLOSSEN || false);
const info_message = ref(window.INFO_MESSAGE || '');

// Die Prüfung benötigt zusätzlich das Geburtsdatum und wird beim Absenden
// serverseitig erneut durchgeführt.
async function checkMitglied(vorname, nachname, geburtsdatum) {
  if (!vorname || !nachname || !geburtsdatum) return false
  try {
    const response = await axios.post(
      `${API_BASE}/mitglied/verifizieren/`,
      { vorname, nachname, geburtsdatum },
      { headers: { 'X-CSRFToken': csrfToken } }
    )
    return response.data.verified === true
  } catch (error) {
    console.warn('Mitgliedsprüfung fehlgeschlagen:', error)
    return false
  }
}

// Lädt Rechtstexte und Artikel für das gewählte Treffen
async function loadTreffenAbhaengigeDaten() {
  if (!treffen.value) {
    alleRechtstexte.value = []
    alleArtikelGruppen.value = []
    tshirtArtikelGruppe.value = []
    uebernachtungArtikelGruppen.value = []
    alleInfotexte.value = []
    return
  }

  // Rechtstexte laden
  const rechtstexteRes = await axios.get(
    `${API_BASE}/rechtstexte/`,
    {
      params: { treffen: treffen.value }
    }
  )
  alleRechtstexte.value = rechtstexteRes.data

  // Infotexte laden
  const infoRes = await axios.get(
    `${API_BASE}/infotexte/`,
    {
      params: { treffen: treffen.value }
    }
  )
  alleInfotexte.value = infoRes.data

  // Artikel laden
  const artikelRes = await axios.get(`${API_BASE}/artikel/`, 
  { 
    params: { treffen: treffen.value }
  })
  alleArtikelGruppen.value = artikelRes.data

  // Filter für Übernachtungs-Gruppen mit den artikel welche "pro Nacht" im Detail haben
  uebernachtungArtikelGruppen.value = artikelRes.data
  .map(g => ({
    ...g,
    artikel: g.artikel.filter(a =>
      a.artikeldetail &&
      a.artikeldetail.toLowerCase().includes('pro nacht')  
    )
  }))
  .filter(g => g.artikel.length > 0)
  console.log('Übernachtungs-Gruppen:', uebernachtungArtikelGruppen.value)

  // Filter für Pauschalartikel-Gruppe
  pauschalArtikelgruppe.value = artikelRes.data.find(g => g.gruppe === 'Pauschal') || ''


  // Filter für T-Shirts-Gruppe
  tshirtArtikelGruppe.value = artikelRes.data.filter(g => g.gruppe === 'T-Shirts')

  // Shopkasse Voreinstellungen: tgo, Tagesgast-Übernachtung, alle Rechtstexte bestätigt
  if (isShopkasse) {
    personalie.gwcdmitglied = ['tgo']
    rechtstexte.value = alleRechtstexte.value.map(rt => rt.id)
    ausgewaehlteArtikelgruppe.value = 'Tagesgast'
    // Personalie-Datensatz "Shopkasse<n>" laden und Formular vorbelegen
    const shopUser = new URLSearchParams(window.location.search).get('shopkasse')
    const shopkasseUser = 'Shopkasse' + shopUser
    try {
      const pRes = await axios.get(`${API_BASE}/personalie/`, {
        params: { nachname: shopkasseUser, treffen: treffen.value }
      })
      if (pRes.data.found) {
        const d = pRes.data
        personalie.gwcdmitglied  = d.gwcdmitglied?.length ? d.gwcdmitglied : ['tgo']
        personalie.mitgliedsnummer = d.mitgliedsnummer || ''
        personalie.vorname        = d.vorname || ''
        personalie.nachname       = d.nachname || ''
        personalie.geburtsdatum   = d.geburtsdatum || ''
        personalie.strasse        = d.strasse || ''
        personalie.plz            = d.plz || ''
        personalie.wohnort        = d.wohnort || ''
        personalie.telefon        = d.telefon || ''
        personalie.email          = d.email || ''
        personalie.bemerkung      = d.bemerkung || ''
        personalie.stammtisch     = d.stammtisch || ''
        personalie.notfall        = d.notfall || ''
        personalie.nottelefon     = d.nottelefon || ''
        personalie.kennzeichen    = d.kennzeichen || ''
        personalie.bikemodel      = d.bikemodel || 'BIKE'
        personalie.bikevariant    = d.bikevariant || 'v'
        personalie.anreisekm      = d.anreisekm || ''
        personalie.tshirt_size    = d.tshirt_size || ''
      }
    } catch (e) {
      console.warn('Shopkasse-Personalie nicht gefunden:', e)
    }
  }
}

// Treffen laden beim Mount
onMounted(async () => {
  const treffenRes = await axios.get(`${API_BASE}/treffen/`, 
    {
      params: { treffen: routeTreffenUuid.value }
    }
  );
  aktTreffen.value = treffenRes.data;
  treffen.value = aktTreffen.value.length ? aktTreffen.value[0].uuid : '';
  console.log('Aktuelles Treffen:', treffen.value);
})

// Auch beim initialen Seitenaufruf laden: Die UUID aus dem Template ist
// identisch mit der UUID aus der Treffen-API und löst sonst keinen Watcher aus.
watch(treffen, loadTreffenAbhaengigeDaten, { immediate: true })

const mitgliedGeprueft = ref(false)
let mitgliedPruefungVersion = 0
watch(
  () => [personalie.stammtisch, personalie.vorname, personalie.nachname, personalie.geburtsdatum],
  async ([stammtisch, vorname, nachname, geburtsdatum]) => {
    const pruefungVersion = ++mitgliedPruefungVersion
    const vornameBereinigt = vorname.trim()
    const nachnameBereinigt = nachname.trim()
    const geburtsdatumBereinigt = geburtsdatum.trim()

    mitgliedGeprueft.value = false
    if (
      !isBarbarossaWinger(stammtisch)
      || !vornameBereinigt
      || !nachnameBereinigt
      || !dateRegex.test(geburtsdatumBereinigt)
    ) return

    const istMitglied = await checkMitglied(
      vornameBereinigt,
      nachnameBereinigt,
      geburtsdatumBereinigt,
    )
    if (pruefungVersion === mitgliedPruefungVersion) {
      mitgliedGeprueft.value = istMitglied
    }
  }
)

const mitreisenderGeprueft = ref(false)
let mitreisenderPruefungVersion = 0
watch(
  () => [
    personalie.stammtisch,
    neuerMitreisender.vorname,
    neuerMitreisender.nachname,
    neuerMitreisender.geburtsdatum,
  ],
  async ([stammtisch, vorname, nachname, geburtsdatum]) => {
    const pruefungVersion = ++mitreisenderPruefungVersion
    const vornameBereinigt = vorname.trim()
    const nachnameBereinigt = nachname.trim()
    const geburtsdatumBereinigt = geburtsdatum.trim()

    mitreisenderGeprueft.value = false
    if (
      !isBarbarossaWinger(stammtisch)
      || !vornameBereinigt
      || !nachnameBereinigt
      || !dateRegex.test(geburtsdatumBereinigt)
    ) return

    const istMitglied = await checkMitglied(
      vornameBereinigt,
      nachnameBereinigt,
      geburtsdatumBereinigt,
    )
    if (pruefungVersion === mitreisenderPruefungVersion) {
      mitreisenderGeprueft.value = istMitglied
    }
  }
)

const bestellSumme = computed(() => {
  let summe = bestellung.value.reduce((sum, b, idx) => {
    const preis = artikelPreis(b.artikel, b.anzahl || 1, personalie.stammtisch, idx) || 0
    const optionPreis = b.option && b.option.option_preis ? parseFloat(b.option.option_preis) : 0
    return sum + preis + (optionPreis * (b.option_anzahl || 0)) + ((b.buffet_preis || 0) * (b.buffet_anzahl || 0))
  }, 0)
  return summe
})


// Funktion zur Aktualisierung der Pauschalartikel-Bestellungen basierend auf Mitreisenden-Anzahl
function updatePauschalArtikelBestellungen() {
  const pauschalArtikel = findePauschalArtikel()
  if (!pauschalArtikel) return
  
  const anzahlMitreisende = mitreisende.value.filter(m => m.vorname || m.nachname).length
  
  // Finde bestehende Mitreisende-Pauschalartikel-Bestellung (E2)
  const mitreisendePauschalIndex = bestellung.value.findIndex(b => 
    b.pauschal && b.option?.option === 'E2' && b.artikel === pauschalArtikel.id
  )
  
  if (anzahlMitreisende > 0) {
    // Es gibt Mitreisende - erstelle oder aktualisiere die E2-Bestellung
    const MitreisendePauschal = pauschalArtikel.optionen.find(o => o.option === 'E2')
    if (MitreisendePauschal) {
      if (mitreisendePauschalIndex >= 0) {
        // Aktualisiere bestehende Bestellung
        bestellung.value[mitreisendePauschalIndex].option_anzahl = anzahlMitreisende
      } else {
        // Erstelle neue Bestellung für Mitreisende
        bestellung.value.unshift({
          artikel: pauschalArtikel.id,
          anzahl: 1,
          option: toRaw(MitreisendePauschal),
          option_anzahl: anzahlMitreisende,
          option_text: MitreisendePauschal.option_text,
          option_preis: MitreisendePauschal.option_preis,
          buffet: null,
          buffet_anzahl: 0,
          buffet_text: '',
          pauschal: true
        })
      }
    }
  } else {
    // Keine Mitreisenden - entferne die E2-Bestellung falls vorhanden
    if (mitreisendePauschalIndex >= 0) {
      bestellung.value.splice(mitreisendePauschalIndex, 1)
    }
  }
}

function updateKindBestellungen() {
  const pauschalArtikel = findePauschalArtikel()
  if (!pauschalArtikel) return

  const anzahlKinder = mitreisende.value.filter(m =>
    (m.vorname || m.nachname) && istKindUnter14(m.geburtsdatum)
  ).length

  const kindPauschalIndex = bestellung.value.findIndex(b =>
    b.pauschal && b.option?.option === 'E3' && b.artikel === pauschalArtikel.id
  )

  if (anzahlKinder > 0) {
    const KindPauschal = pauschalArtikel.optionen.find(o => o.option === 'E3')
    if (KindPauschal) {
      if (kindPauschalIndex >= 0) {
        bestellung.value[kindPauschalIndex].option_anzahl = anzahlKinder
      } else {
        bestellung.value.unshift({
          artikel: pauschalArtikel.id,
          anzahl: 1,
          option: toRaw(KindPauschal),
          option_anzahl: anzahlKinder,
          option_text: KindPauschal.option_text,
          option_preis: KindPauschal.option_preis,
          buffet: null,
          buffet_anzahl: 0,
          buffet_text: '',
          pauschal: true
        })
      }
    }
  } else {
    if (kindPauschalIndex >= 0) {
      bestellung.value.splice(kindPauschalIndex, 1)
    }
  }
}

function updateTshirtArtikelBarbarossa() {
    if (isBarbarossaWinger(personalie.stammtisch)) {
      // Remove alle bestehenden T-Shirt-Bestellungen mit option 'T0', da wir sie basierend auf der neuen Personenanzahl neu berechnen
      bestellung.value = bestellung.value.filter(b => !(b.artikel && istTShirtArtikel(b.artikel) && b.option?.option === 'T0'))

      const tshirtGruppe = tshirtArtikelGruppe.value.length ? tshirtArtikelGruppe.value[0] : null
      if (tshirtGruppe) {
        const tshirtFahrer = tshirtGruppe.artikel.find(a => 
          a.artikelname && (
            a.artikelname.toLowerCase().includes(TYP_TSHIRT_SIZE[personalie.tshirt_size].toLowerCase())
          )
        )
        
        if (tshirtFahrer) {
           const tshirtoption = tshirtFahrer.optionen.find(o => o.option === 'T0')
           bestellung.value.push({
            artikel: tshirtFahrer.id,
            anzahl: 1,
            artikelPreis: tshirtFahrer.preis,
            option: tshirtoption ? toRaw(tshirtoption) : null,
            option_anzahl: 1,
            option_text: tshirtoption ? tshirtoption.option_text : '',
            buffet: null,
            buffet_anzahl: 0,
            buffet_text: ''
          })
        }
        for (const mitreisender of mitreisende.value) {
          if (mitreisender.vorname || mitreisender.nachname) {
            const tshirtMitreisender = tshirtGruppe.artikel.find(a => 
              a.artikelname && (
                a.artikelname.toLowerCase().includes(TYP_TSHIRT_SIZE[mitreisender.tshirt_size].toLowerCase())
              )
            )
            if (tshirtMitreisender) {
              const tshirtoption = tshirtMitreisender.optionen.find(o => o.option === 'T0')
              bestellung.value.push({
                artikel: tshirtMitreisender.id,
                anzahl: 1,
                artikelPreis: tshirtMitreisender.preis,
                option: tshirtoption ? toRaw(tshirtoption) : null,
                option_anzahl: 1,
                option_text: tshirtoption ? tshirtoption.option_text : '',
                option_preis: tshirtoption ? tshirtoption.option_preis : 0,
                buffet: null,
                buffet_anzahl: 0,
                buffet_text: ''
              })
              console.log('T-Shirt für Mitreisenden hinzugefügt:', mitreisender.vorname, mitreisender.nachname, 'Artikel:', tshirtMitreisender.artikelname)
            }
          }
        }
      }
    }
}

function addMitreisender() {
  // Prüfe, ob eines der Felder ausgefüllt ist
  if (
    neuerMitreisender.vorname ||
    neuerMitreisender.nachname ||
    neuerMitreisender.geburtsdatum
  ) {
    // Prüfe, ob alle Felder ausgefüllt sind
    if (
      !neuerMitreisender.vorname ||
      !neuerMitreisender.nachname ||
      !neuerMitreisender.geburtsdatum
    ) {
      alert('Bitte füllen Sie Vorname, Nachname und Geburtsdatum für Mitreisende aus.');
      return;
    }
    mitreisende.value.push({ ...neuerMitreisender, istMitglied: mitreisenderGeprueft.value });
    neuerMitreisender.vorname = '';
    neuerMitreisender.nachname = '';
    neuerMitreisender.mitgliedsnummer = '';
    neuerMitreisender.geburtsdatum = '';
    neuerMitreisender.tshirt_size = '';
    
    if (ausgewaehlteArtikelgruppe.value) {
      // Überprüfe und füge Mitreisende-Pauschalartikel hinzu, falls nötig
      if (bestellung.value.filter(b => b.pauschal === true).length > 0) {
        updatePauschalArtikelBestellungen();
      }
      // Kind < 14 Jahre Rabatt aktualisieren
      updateKindBestellungen();
      // Aktualisiere T-Shirt-Bestellungen für Barbarossa-Winger
      if (isBarbarossaWinger(personalie.stammtisch)) {
        updateTshirtArtikelBarbarossa()
      }
    }

  } else {
    alert('Bitte füllen Sie mindestens eines der Felder (Vorname, Nachname oder Geburtsdatum) aus, um einen Mitreisenden hinzuzufügen.');
  }
}

function removeMitreisender(idx) {
  mitreisende.value.splice(idx, 1)
  // Überprüfe und entferne Mitreisende-Pauschalartikel, falls nötig
  updatePauschalArtikelBestellungen();
  // Kind < 14 Jahre Rabatt aktualisieren
  updateKindBestellungen();
  // Aktualisiere T-Shirt-Bestellungen für Barbarossa-Winger  
  if (isBarbarossaWinger(personalie.stammtisch)) {
    updateTshirtArtikelBarbarossa()
  }
}

function addBestellung() {
  bestellung.value.push({ artikel: '', anzahl: 1, option: null, option_anzahl: 0, option_text: '', buffet: 'N', buffet_anzahl: 0, buffet_text: '' })
  console.log('Bestellung hinzugefügt:', bestellung.value);
}

function removeBestellung(idx) {
  bestellung.value.splice(idx, 1)
}

function validateAnzahl(b) {
  if (b.anzahl < 1) {
    b.anzahl = 1
  }
}

const alleArtikel = computed(() =>
  alleArtikelGruppen.value.flatMap(g => g.artikel)
)

// Berechne T-Shirt-Preise global für alle Positionen
const tshirtPreisMap = computed(() => {
  const preisMap = new Map()
  
  if (isBarbarossaWinger(personalie.stammtisch)) {
    const maxKostenlos = maxTShirtAnzahl.value
    let verbleibendeKostenlose = maxKostenlos
    
    // Alle T-Shirt-Positionen sammeln
    bestellung.value.forEach((b, index) => {
      if (b.artikel && istTShirtArtikel(b.artikel)) {
        const artikel = alleArtikel.value.find(a => a.id === b.artikel)
        if (artikel) {
          const anzahl = b.anzahl || 1
          const kostenloseAnzahl = Math.min(anzahl, verbleibendeKostenlose)
          const kostenpflichtigeAnzahl = anzahl - kostenloseAnzahl
          const preis = artikel.preis * kostenpflichtigeAnzahl
          
          preisMap.set(index, preis)
          verbleibendeKostenlose -= kostenloseAnzahl
        }
      }
    })
  }
  
  return preisMap
})

const artikelPreis = (artikelId, anzahl, stammtisch = '', bestellungsIndex = null) => {
  const artikel = alleArtikel.value.find(a => a.id === artikelId)
  let artikelpreis = artikel ? (artikel.preis * anzahl) : 0  
  return artikelpreis
}

const buchungZusammenfassung = ref(null)
const buchungHtml = ref('')
const isSubmitting = ref(false)

async function handleSubmit() {
  
  isSubmitting.value = true
  const payload = {
    treffen: treffen.value,
    registration_token: window.REGISTRATION_TOKEN,
    shopkasse: isShopkasse,
    personalie: { ...personalie },
    mitreisende: mitreisende.value,
    bestellung: bestellung.value.map((b, idx) => {
      const artikel = alleArtikel.value.find(a => a.id === b.artikel)
      const option_array = []

      // Option hinzufügen, falls vorhanden
      if (b.option && b.option !== 'N') {
        option_array.push({
          type: 'option',
          value: b.option.option ?? b.option,
          text: b.option.option_text ?? '',
          preis: b.option.option_preis ?? 0,
          anzahl: b.option_anzahl ?? 0
        })
      }

      // Buffet hinzufügen, falls vorhanden
      if (b.buffet && b.buffet !== 'N') {
        option_array.push({
          type: 'buffet',
          value: b.buffet,
          text: b.buffet_text ?? '',
          preis: b.buffet_preis ?? 0,
          anzahl: b.buffet_anzahl ?? 0
        })
      }
      const istUebernachtung = istUebernachtungsArtikel(b.artikel)
      return {
        artikelId: artikel ? artikel.id : null,
        istUebernachtung: istUebernachtung,
        anzahl: b.anzahl,
        preis: artikel ? artikelPreis(b.artikel, 1, personalie.stammtisch, idx) : 0,
        option_array,
      }
    }),
    rechtstexte: rechtstexte.value
  }
  try {
    const response = await axios.post(`${API_BASE}/anmeldung/`, 
    payload,
    {
      headers: { 
        'Content-Type': 'application/json',
        'X-CSRFToken': csrfToken
      }
    })
    
    // HTML aus der Anmeldung-Response verwenden
    if (response.data.html) {
      buchungHtml.value = response.data.html
    }
    
    // Zusammenfassung erzeugen für den Fall, dass kein HTML zurückgegeben wird
    buchungZusammenfassung.value = {
      treffen: aktTreffen.value.find(t => t.uuid === treffen.value),
      personalie: { ...personalie },
      mitreisende: mitreisende.value.filter(m => m.vorname || m.nachname || m.mitgliedsnummer || m.geburtsdatum),
      bestellung: bestellung.value.map((b, idx) => {
        const artikel = alleArtikel.value.find(a => a.id === b.artikel)
        const option_array = []

        // Option hinzufügen, falls vorhanden
        if (b.option && b.option !== 'N') {
          option_array.push({
            type: 'option',
            value: b.option.option ?? b.option,
            text: b.option.option_text ?? '',
            preis: b.option.option_preis ?? 0,
            anzahl: b.option_anzahl ?? 0
          })
        }

        // Buffet hinzufügen, falls vorhanden
        if (b.buffet && b.buffet !== 'N') {
          option_array.push({
            type: 'buffet',
            value: b.buffet,
            text: b.buffet_text ?? '',
            preis: b.buffet_preis ?? 0,
            anzahl: b.buffet_anzahl ?? 0
          })
        }

        return {
          artikelId: artikel ? artikel.id : null,
          artikelname: artikel ? artikel.artikelname : '',
          artikeldetail: artikel ? artikel.artikeldetail : '',
          anzahl: b.anzahl,
          preis: artikel ? artikelPreis(b.artikel, 1, personalie.stammtisch, idx) : 0,
          option_array,
          gesamt: artikel
            ? (b.anzahl * artikelPreis(b.artikel, b.anzahl, personalie.stammtisch, idx)) +
              option_array.reduce((sum, o) => sum + (o.preis * (o.anzahl || 0)), 0)
            : 0
        }
      }),
      rechtstexte: alleRechtstexte.value.filter(rt => rechtstexte.value.includes(rt.id))
    }
    
    // Formular zurücksetzen (optional)
  } catch (e) {
    let msg = 'Fehler bei der Anmeldung.\n'
    if (e.response && e.response.data) {
      if (e.response.data.error) {
        msg += e.response.data.error
      }
      if (e.response.data.exception_value) {
        msg += '\n' + e.response.data.exception_value
      }
      // Erweiterte Ausgabe
      if (e.response.status) {
        msg += `\nStatus: ${e.response.status}`
      }
      if (e.response.statusText) {
        msg += `\nStatusText: ${e.response.statusText}`
      }
      if (typeof e.response.data === 'string') {
        msg += `\nResponse: ${e.response.data}`
      } else if (typeof e.response.data === 'object') {
        msg += `\nResponse: ${JSON.stringify(e.response.data)}`
      }
    } else if (e.message) {
      msg += '\n' + e.message
    }
    alert(msg)
  } finally {
    isSubmitting.value = false
  }
}


const alleRechtstexteBestaetigt = computed(() => 
  alleRechtstexte.value.length > 0 &&
  rechtstexte.value.length === alleRechtstexte.value.length
)

// Berechne die maximale erlaubte T-Shirt-Anzahl (nur bestätigte Mitglieder)
const maxTShirtAnzahl = computed(() => {
  const fahrerAnzahl = mitgliedGeprueft.value ? 1 : 0
  const mitreisendeMitglied = mitreisende.value.filter(m => (m.vorname || m.nachname) && m.istMitglied).length
  return fahrerAnzahl + mitreisendeMitglied
})

// Berechne die aktuell bestellte T-Shirt-Anzahl
const aktuelleTShirtAnzahl = computed(() => {
  return bestellung.value.reduce((sum, b) => {
    if (b.artikel && istTShirtArtikel(b.artikel)) {
      return sum + (b.anzahl || 0)
    }
    return sum
  }, 0)
})

// Prüfe ob T-Shirt-Limit erreicht ist
const tshirtLimitErreicht = computed(() => {
  return aktuelleTShirtAnzahl.value >= maxTShirtAnzahl.value
})

const routeTreffenUuid = computed(() => {
  const match = window.location.pathname.match(/treffen\/([0-9a-fA-F-]+)/);
  return match ? match[1] : null;
});

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return dateStr; // default ist de-DE Format
  // if (isNaN(d)) return dateStr;
  // return d.toLocaleDateString('de-DE', {
  //   year: 'numeric',
  //   month: '2-digit',
  //   day: '2-digit'
  // });
}

function formatDate2(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
    return d.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
  });
}

const gefilterteArtikel = ref([])

const aktuellesTreffen = computed(() =>
  aktTreffen.value.find(t => t.uuid === treffen.value)
)

async function onArtikelgruppeSelected() {
  if (!ausgewaehlteArtikelgruppe.value) {
    console.log('Keine Artikelgruppe ausgewählt!');
    return;
  }
  // Hier deine Logik, wenn eine Gruppe gewählt wurde
  // console.log('Gewählte Artikelgruppe:', ausgewaehlteArtikelgruppe.value);
  const gruppe = alleArtikelGruppen.value.find(g => g.gruppe === ausgewaehlteArtikelgruppe.value)
  const uebernachtungsArtikel = gruppe ? gruppe.artikel : []
  // T-Shirt-Artikel ergänzen
  const tshirtArtikel = tshirtArtikelGruppe.value.length ? tshirtArtikelGruppe.value[0].artikel : []
  // Pauschal-Artikel ergänzen
  const pauschalArtikel = findePauschalArtikel()
  const shopArtikel = alleArtikelGruppen.value.find(g => g.gruppe === 'Shop')?.artikel || []  
  const anzahlMitreisende = mitreisende.value.filter(m => m.vorname || m.nachname).length
  const FahrerPauschal = pauschalArtikel.optionen.find(o => o.option === 'E1')

  // setze die Artikel für die Anzeige basierend auf der gewählten Gruppe
  gefilterteArtikel.value = uebernachtungsArtikel.concat(tshirtArtikel, pauschalArtikel ? [pauschalArtikel] : [], shopArtikel )

  // Bestellung leeren (außer bereits vorhandene Pauschal-Artikel)
  bestellung.value = bestellung.value.filter(b => b.pauschal === true)

  // Übernachtungsartikel aus der gewählten Gruppe hinzufügen
  if (gruppe && gruppe.artikel.length > 0) {
    // Nimm den ersten Übernachtungsartikel aus der Gruppe
    const firstuebernachtungsartikel = gruppe.artikel.find(a => a.artikeldetail && (a.artikeldetail.toLowerCase().includes('pro nacht') || a.artikeldetail.toLowerCase().includes('pro tag')))
    if (firstuebernachtungsartikel) {
      bestellung.value.push({
        artikel: firstuebernachtungsartikel.id,
        anzahl: personalie.gwcdmitglied.includes('tgo') ? 1 + anzahlMitreisende : 1,
        option: null,
        option_anzahl: 0,
        option_text: '',
        buffet: null,
        buffet_anzahl: 0,
        buffet_text: ''
      })
      console.log('Übernachtungsartikel hinzugefügt:', firstuebernachtungsartikel.artikelname)
    }
  }

  if (personalie.gwcdmitglied.includes('tgo')) {
    console.log('Tagesgast ohne Einschreibung')
    artikelvz = shopArtikel.find(a => a.artikelname === 'Verzehrkarte')
      if (artikelvz) {
        bestellung.value.push({
          artikel: artikelvz.id,
          anzahl: 1,
          option: null,
          option_anzahl: 0,
          option_text: '',
          buffet: null,
          buffet_anzahl: 0,
          buffet_text: ''
        })
        console.log('Verzehrkarte für Tagesgast hinzugefügt:', artikelvz.artikelname)
      } else {
        console.log('Keine Verzehrkarte für Tagesgast gefunden!')
      }
    return
  }

  // Pauschalartikel hinzufügen (falls noch nicht vorhanden)
  if (pauschalArtikel && !bestellung.value.some(b => b.artikel === pauschalArtikel.id)) {
    if (anzahlMitreisende > 0) {
      const MitreisendePauschal = pauschalArtikel.optionen.find(o => o.option === 'E2')
      bestellung.value.unshift({
        artikel: pauschalArtikel.id,
        anzahl: 1,
        option: toRaw(MitreisendePauschal),
        option_anzahl: anzahlMitreisende,
        option_text: MitreisendePauschal.option_text,
        option_preis: MitreisendePauschal.option_preis,
        buffet: null,
        buffet_anzahl: 0,
        buffet_text: '',
        pauschal: true
      })
      console.log('Einschreibung Mitreisende-Pauschal hinzugefügt:', MitreisendePauschal.option_text)
    } 

    if (FahrerPauschal) {
      console.log(`Pauschal-Artikel E1 gefunden: ${FahrerPauschal ? 'Ja' : 'Nein'}`)
      bestellung.value.unshift({
        artikel: pauschalArtikel.id,
        anzahl: 1,
        option: toRaw(FahrerPauschal),
        option_anzahl: 1,
        option_text: FahrerPauschal.option_text,
        option_preis: FahrerPauschal.option_preis,
        buffet: null,
        buffet_anzahl: 0,
        buffet_text: '',
        pauschal: true
      })
      console.log('Einschreibung Fahrer-Pauschal hinzugefügt:', FahrerPauschal.option_text)
    }
  }

  // Kind < 14 Jahre Rabatt hinzufügen
  updateKindBestellungen()

  // 3. T-Shirts  hinzufügen (erlaubte Menge basierend auf Personenanzahl)
  if (isBarbarossaWinger(personalie.stammtisch)) {
     console.log('Stammtisch ist Barbarossa-Winger, prüfe T-Shirt Hinzufügung...')
    const tshirtGruppe = tshirtArtikelGruppe.value.length ? tshirtArtikelGruppe.value[0] : null
    if (tshirtGruppe) {
      // Finde T-Shirt  Artikel
      const tshirtFahrer = tshirtGruppe.artikel.find(a => 
        a.artikelname && (
          a.artikelname.toLowerCase().includes(TYP_TSHIRT_SIZE[personalie.tshirt_size].toLowerCase())
        )
      )
      
      const istMitglied = await checkMitglied(personalie.vorname, personalie.nachname, personalie.geburtsdatum)
      if (tshirtFahrer && istMitglied) {
        const tshirtoption = tshirtFahrer.optionen.find(o => o.option === 'T0')
        if (tshirtoption) {
          console.log(`T-Shirt Option T0 gefunden: ${tshirtoption.option_text} (${tshirtoption.option_preis} €)`)
        } else {
          console.log('Keine passende T-Shirt Option T0 gefunden!')
        }
        console.log(`T-Shirt Artikel gefunden: ${tshirtFahrer.artikelname} (ID: ${tshirtFahrer.id})`)
        bestellung.value.push({
          artikel: tshirtFahrer.id,
          anzahl: 1,
          artikelPreis: tshirtFahrer.preis,
          option: tshirtoption ? toRaw(tshirtoption) : null,
          option_anzahl: 1,
          option_text: tshirtoption ? tshirtoption.option_text : '',
          option_preis: tshirtoption ? tshirtoption.option_preis : 0,
          buffet: null,
          buffet_anzahl: 0,
          buffet_text: ''
        })
        console.log('T-Shirt hinzugefügt:', tshirtFahrer.artikelname, 'Anzahl:', 1)
      }
      for (const mitreisender of mitreisende.value) {
        if (mitreisender.vorname || mitreisender.nachname) {
          const tshirtMitreisender = tshirtGruppe.artikel.find(a => 
            a.artikelname && (
              a.artikelname.toLowerCase().includes(TYP_TSHIRT_SIZE[mitreisender.tshirt_size].toLowerCase())
            )
          )
          const istMitreisenderMitglied = await checkMitglied(
            mitreisender.vorname,
            mitreisender.nachname,
            mitreisender.geburtsdatum
          )
          if (tshirtMitreisender && istMitreisenderMitglied) {
            const tshirtoption = tshirtMitreisender.optionen.find(o => o.option === 'T0')
            bestellung.value.push({
              artikel: tshirtMitreisender.id,
              anzahl: 1,
              artikelPreis: tshirtMitreisender.preis,
              option: tshirtoption ? toRaw(tshirtoption) : null,
              option_anzahl: 1,
              option_text: tshirtoption ? tshirtoption.option_text : '',
              option_preis: tshirtoption ? tshirtoption.option_preis : 0,
              buffet: null,
              buffet_anzahl: 0,
              buffet_text: ''
            })
            console.log('T-Shirt für Mitreisenden hinzugefügt:', mitreisender.vorname, mitreisender.nachname, 'Artikel:', tshirtMitreisender.artikelname)
          }
        }
      }
    }
  } else {
    console.log('Stammtisch ist nicht Barbarossa-Winger, überspringe T-Shirt Hinzufügung.')
  }


  // 4. Verzehrkarte hinzufügen 
  artikelvz = shopArtikel.find(a => a.artikelname === 'Verzehrkarte')
  if (artikelvz) {
    bestellung.value.push({
      artikel: artikelvz.id,
      anzahl: 1,
      option: null,
      option_anzahl: 0,
      option_text: '',
      buffet: null,
      buffet_anzahl: 0,
      buffet_text: ''
    })
    console.log('Verzehrkarte hinzugefügt:', artikelvz.artikelname)
  } else {
    console.log('Keine Verzehrkarte gefunden!')
  }

  console.log('Auto-Insert abgeschlossen. Neue Bestellung:', bestellung.value)
}

function parseGermanDate(str) {
  if (!str) return null;
  // Prüfe auf deutsches Format
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(str)) {
    const [tag, monat, jahr] = str.split('.');
    return new Date(`${jahr}-${monat}-${tag}`);
  }
  // Prüfe auf ISO-Format
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    return new Date(str);
  }
  return null;
}

function istKindUnter14(geburtsdatum) {
  if (!geburtsdatum) return false
  const geb = parseGermanDate(geburtsdatum)
  if (!geb || isNaN(geb)) return false
  const referenz = aktuellesTreffen.value?.zeitraum_von
    ? (parseGermanDate(aktuellesTreffen.value.zeitraum_von) || new Date())
    : new Date()
  const alter = referenz.getFullYear() - geb.getFullYear()
    - ((referenz.getMonth() < geb.getMonth() ||
       (referenz.getMonth() === geb.getMonth() && referenz.getDate() < geb.getDate())) ? 1 : 0)
  return alter < 14
}

function istUebernachtungsArtikel(artikelId) {
  const gruppenartikel = getArtikelGruppenById(artikelId)
  const uebernachtungsartikel = findeUebernachtungsArtikelById(artikelId)
  return !!(
    uebernachtungsartikel &&
    gruppenartikel.some(g => ['Hotel', 'Camping'].includes(g))
  )
}

function istTShirtArtikel(artikelId) {
  const gruppenartikel = getArtikelGruppenById(artikelId)
  const tshirtArtikel = findeTShirtArtikelById(artikelId)
  return !!(
    tshirtArtikel &&
    gruppenartikel.some(g => ['T-Shirts', 'T-Shirt'].includes(g))
  )
}

function bestellText(b) {
  if (b.artikel) {
    const artikel = alleArtikel.find(a => a.id === b.artikel)
    if (artikel) {
      const gruppenartikel = getArtikelGruppenById(b.artikel)
      const istUebernachtung = istUebernachtungsArtikel(b.artikel)
      console.log(`Bestell Artikel: ${b.artikel}, ArtikelGruppen: ${gruppenartikel.join(', ')}, Ist Übernachtung: ${istUebernachtung}`)
      if (istUebernachtung) {
        result = `Anzahl: 1, ${b.anzahl} Nächte, `
        if (b.option_anzahl && aktuellesTreffen.value && aktuellesTreffen.value.verl_zimmer) {
          result += `+ ${b.option_anzahl} Nächte Verlängerung, ${b.option_text} ${b.option_preis} €  `
        }
        if (b.buffet_anzahl && b.buffet_text) {
          result += `+ ${b.buffet_text} ${b.buffet_preis} € `
        } 
        return result
      } else {
        return `Anzahl: ${b.anzahl}, `
      }
    }
  } 
}

// Beobachtet die Auswahl der Artikelgruppe und setzt die Anreise- und Abreisedaten
const prevGruppe = ref(null)

const heutigesDatum = computed(() => {
  const heute = new Date()
  const tag = String(heute.getDate()).padStart(2, '0')
  const monat = String(heute.getMonth() + 1).padStart(2, '0')
  const jahr = heute.getFullYear()
  
  return `${tag}.${monat}.${jahr}`
})



watch(ausgewaehlteArtikelgruppe, (gruppe) => {
  // Bestellung leeren, wenn die Gruppe gewechselt wurde
  if (prevGruppe && gruppe !== prevGruppe ) {
    if (prevGruppe.value === 'Tagesgast') {
      personalie.anreisetag = ''
      personalie.abreisetag = ''
    }
    bestellung.value = [{ artikel: '', anzahl: 1, option: null , option_anzahl: 0, buffet: null, buffet_anzahl: 0 }]
  }
  prevGruppe.value = gruppe

  if (gruppe && aktuellesTreffen.value && personalie.anreisetag === '' && personalie.abreisetag === '' )  {
    personalie.anreisetag = aktuellesTreffen.value.zeitraum_von || ''
    personalie.abreisetag = aktuellesTreffen.value.zeitraum_bis || ''
  }
  if (gruppe && aktuellesTreffen.value && gruppe === 'Tagesgast') {
    personalie.anreisetag = heutigesDatum.value
    personalie.abreisetag = heutigesDatum.value
  }
  // Prüfe, ob neuerMitreisender ausgefüllt ist und füge ihn ggf. hinzu
  if (
    neuerMitreisender.vorname ||
    neuerMitreisender.nachname ||
    neuerMitreisender.mitgliedsnummer ||
    neuerMitreisender.geburtsdatum
  ) {
    addMitreisender();
  }

  onArtikelgruppeSelected()
  console.log('In Bestellungen:', bestellung.value)

})

function getArtikelGruppenById(artikelId) {
  const gruppen = []
  for (const gruppe of alleArtikelGruppen.value) {
    const artikel = gruppe.artikel.find(a => a.id === artikelId)
    if (artikel) {
      if (artikel.artikelgruppen && Array.isArray(artikel.artikelgruppen)) {
        artikel.artikelgruppen.forEach(g => {
          if (!gruppen.includes(g.gruppe)) gruppen.push(g.gruppe)
        })
      } else if (!gruppen.includes(gruppe.gruppe)) {
        gruppen.push(gruppe.gruppe)
      }
    }
  }
  return gruppen
}

// Hilfsfunktion: Finde Pauschal-Artikel
function findePauschalArtikel() {
  return alleArtikel.value.find(a => a.pauschal !== 'N')
}

function findeShopArtikel() {
  const shopArtikel = alleArtikelGruppen.value.find(g => g.gruppe === 'Shop')?.artikel || []
  return shopArtikel
}

function istShopArtikel(artikelId) {
  const gruppenartikel = getArtikelGruppenById(artikelId)
  return gruppenartikel.some(g => g === 'Shop')
}

// Watch auf ersten Artikel in der Bestellung
watch(
  () => bestellung.value[0]?.artikel,
  (artikelId) => {
    // Prüfe, ob neuerMitreisender ausgefüllt ist und füge ihn ggf. hinzu
    if (
      neuerMitreisender.vorname ||
      neuerMitreisender.nachname ||
      neuerMitreisender.mitgliedsnummer ||
      neuerMitreisender.geburtsdatum
    ) {
      addMitreisender();
    }
  }
)

function findeUebernachtungsArtikelById(artikelId) {
  for (const gruppe of uebernachtungArtikelGruppen.value) {
    const artikel = gruppe.artikel.find(a => a.id === artikelId)
    if (artikel) {
      return artikel
    }
  }
  return null
}

function findeTShirtArtikelById(artikelId) {
  for (const gruppe of tshirtArtikelGruppe.value) {
    const artikel = gruppe.artikel.find(a => a.id === artikelId)
    if (artikel) {
      return artikel
    }
  }
  return null
}

// aktualisiert die Anzahl der bestellten Artikel basierend auf dem Datum
watch(
  () => [personalie.anreisetag, personalie.abreisetag, ...bestellung.value.map(b => b.artikel)],
  () => {
    const von = parseGermanDate(personalie.anreisetag)
    const bis = parseGermanDate(personalie.abreisetag)
    const event_von = parseGermanDate(aktuellesTreffen.value.zeitraum_von)
    const event_bis = parseGermanDate(aktuellesTreffen.value.zeitraum_bis)

    if (von && bis && !isNaN(von) && !isNaN(bis)) {
      const diff = Math.max(1, Math.ceil((bis - von) / (1000 * 60 * 60 * 24)))
      const event_diff = Math.max(1, Math.ceil((event_bis - event_von) / (1000 * 60 * 60 * 24)))
      let option_diff = 0
      if (diff > event_diff) {
        option_diff = diff - event_diff
      }
      // console.log(`Diff: ${diff}, Event-Diff: ${event_diff}, Option-Diff: ${option_diff}`)
      if (!aktuellesTreffen.value.verl_zimmer && option_diff > 0) {
        alert('Eine Verlängerung der Zimmerbuchung ist nicht möglich');
        option_diff = 0
        personalie.anreisetag = aktuellesTreffen.value.zeitraum_von
        personalie.abreisetag= aktuellesTreffen.value.zeitraum_bis
        return
      }

      bestellung.value.forEach(b => {
        if (b.artikel) {
          console.log(`Neu Artikel: ${b.artikel}, Anreisetag: ${personalie.anreisetag}, Abreisetag: ${personalie.abreisetag}, Diff: ${diff}`)
          // Finde den Artikel und prüfe Artikeldetail
          const gruppenartikel = getArtikelGruppenById(b.artikel)
          const uebernachtungsartikel = findeUebernachtungsArtikelById(b.artikel)
          const istUebernachtung = !!(
            uebernachtungsartikel &&
            gruppenartikel.some(g => ['Hotel', 'Camping'].includes(g))
          )
          if (istUebernachtung) {
            b.option_anzahl = 0
            b.option_text = ''
            b.option_preis = 0
            b.anzahl = istUebernachtung ? diff - option_diff : 1
            b.option_anzahl = istUebernachtung ? option_diff : 0
            if (b.option_anzahl > 0) {
              const verlängerungsOption = uebernachtungsartikel.optionen?.find(o => o.option.startsWith('F'))
              if (verlängerungsOption) {
                b.option = verlängerungsOption
                b.option_text = verlängerungsOption.option_text
                b.option_preis = verlängerungsOption.option_preis
              }
            } 
            if (b.anzahl > 0) {
              const stromOption = uebernachtungsartikel.optionen?.find(o => o.option.startsWith('S'))
              if (stromOption) {
                b.option = stromOption
                b.option_text = stromOption.option_text
                b.option_preis = stromOption.option_preis
                b.option_anzahl = b.anzahl + b.option_anzahl
              }
            }
            
            const buffetartikel = findeUebernachtungsArtikelById(b.artikel)
            if (buffetartikel) {
              // Einmal Buffet-Artikel prüfen
              buffet_artikel_b1 = buffetartikel.optionen?.find(o => o.option === 'B1')
              console.log(`Buffet-Artikel B1 gefunden: ${buffet_artikel_b1 ? 'Ja' : 'Nein'}`)
              if (buffet_artikel_b1) {
                b.buffet = buffet_artikel_b1 ? 'B1' : 'N'
                b.buffet_anzahl = buffet_artikel_b1?.option_anzahl ?? 1
                b.buffet_preis = buffet_artikel_b1?.option_preis ?? 0
                b.buffet_text = buffet_artikel_b1?.option_text ?? ''
              }
              // Pro Nacht Buffet-Artikel prüfen
              buffet_artikel_b2 = buffetartikel.optionen?.find(o => o.option === 'B2')
              console.log(`Buffet-Artikel B2 gefunden: ${buffet_artikel_b2 ? 'Ja' : 'Nein'}`)
              if (buffet_artikel_b2) {
                b.buffet = buffet_artikel_b2 ? 'B2' : 'N'
                b.buffet_anzahl = b.anzahl + b.option_anzahl ?? 1
                b.buffet_preis = buffet_artikel_b2?.option_preis ?? 0
                b.buffet_text = buffet_artikel_b2?.option_text ?? ''
              }
            }
          }        
          console.log(`Neu Artikel: ${b.artikel}, ArtikelGruppen: ${gruppenartikel.join(', ')}, Ist Übernachtung: ${istUebernachtung}, anzahl: ${b.anzahl}, option_anzahl: ${b.option_anzahl}`)
        }
      })
    }
  }
)

function validateGeburtsdatum(val, field) {
  if (val) {
    const trimmedVal = val.trim();
    
    // Aktualisiere das Feld mit dem getrimmten Wert
    if (field === 'personalie') personalie.geburtsdatum = trimmedVal;
    if (field === 'mitreisender') neuerMitreisender.geburtsdatum = trimmedVal;
    if (field === 'anreisetag') personalie.anreisetag = trimmedVal;
    if (field === 'abreisetag') personalie.abreisetag = trimmedVal;
    
    // Validierung mit dem getrimmten Wert
    if (!dateRegex.test(trimmedVal)) {
      alert('Bitte geben Sie das Geburtsdatum im Format TT.MM.JJJJ ein.');
      if (field === 'personalie') personalie.geburtsdatum = '';
      if (field === 'mitreisender') neuerMitreisender.geburtsdatum = '';
      if (field === 'anreisetag') personalie.anreisetag = '';
      if (field === 'abreisetag') personalie.abreisetag = '';
      return;
    }

    // Geburtsdatum darf nicht in der Zukunft liegen
    if (field === 'personalie' || field === 'mitreisender') {
      const parsed = parseGermanDate(trimmedVal);
      if (parsed && parsed > new Date()) {
        alert('Das Geburtsdatum darf nicht in der Zukunft liegen.');
        if (field === 'personalie') personalie.geburtsdatum = '';
        if (field === 'mitreisender') neuerMitreisender.geburtsdatum = '';
      }
    }
  }
}

// aktualisiert die Optionen der Bestellung basierend auf den Artikel-IDs
watch(
  bestellung,
  (bestellArr) => {
    bestellArr.forEach(b => {
      if (b.artikel && b.option === null) {
        const artikel = alleArtikel.value.find(a => a.id === b.artikel)
        if (artikel && artikel.optionen?.length > 0) {
          b.option = artikel.optionen[0]
        }
      }
      // Automatische Anzahl für "pro Person" Artikel
      if (b.artikel) {
        const artikel = alleArtikel.value.find(a => a.id === b.artikel)
        if (
          !personalie.gwcdmitglied.includes('tgo') &&
          artikel &&
          artikel.artikeldetail &&
          artikel.artikeldetail.toLowerCase().includes('pro person')
        ) {
          const anzahlMitreisende = mitreisende.value.filter(m => m.vorname || m.nachname).length
          const anzahlPersonen = 1 + anzahlMitreisende
          if (b.artikel.options?.length > 0) {
            if (b.option == 'E1') {
              b.option.option_anzahl = 1
              b.anzahl = 1
              b.option_anzahl = 1
            }
            else if (b.option == 'E2') {
              b.option.option_anzahl = anzahlMitreisende
              b.anzahl = 1
              b.option_anzahl = anzahlMitreisende
            }
            else {
              b.anzahl = anzahlPersonen
              b.option_anzahl = 0 
          }
        }
        // Validierung für T-Shirt-Artikel
        if (istTShirtArtikel(b.artikel)) {
          const maxAnzahl = maxTShirtAnzahl.value
          if (b.anzahl > maxAnzahl) {
            b.anzahl = Math.min(b.anzahl, maxAnzahl)
            alert(`Die T-Shirt-Anzahl wurde auf ${maxAnzahl} begrenzt (Anzahl Personen: ${maxAnzahl})`)
          }
        }
      }
    }
    })
  },
  { deep: true }
)

// Watch auf mitreisende für "pro Person" Artikel und Pauschalartikel
watch(
  mitreisende,
  () => {
    const anzahlMitreisende = mitreisende.value.filter(m => m.vorname || m.nachname).length
    
    bestellung.value.forEach(b => {
      if (b.artikel) {
        const artikel = alleArtikel.value.find(a => a.id === b.artikel)
        
        // Behandlung für "pro person" Artikel
        if (
          !personalie.gwcdmitglied.includes('tgo') &&
          artikel &&
          artikel.artikeldetail &&
          artikel.artikeldetail.toLowerCase().includes('pro person')
        ) {
          const anzahlPersonen = 1 + anzahlMitreisende
          if (artikel.optionen?.length > 0) {
            if (b.option?.option === 'E1') {
              b.option_anzahl = 1
              b.anzahl = 1
            }
            else if (b.option?.option === 'E2') {
              b.option_anzahl = anzahlMitreisende
              b.anzahl = 1
            }
            else {
              b.anzahl = anzahlPersonen
              b.option_anzahl = 0
            }
          }
        }
      }
    })
  },
  { deep: true }
)

</script>

<template>
  <div v-if="anmeldung_geschlossen" class="alert alert-warning">
    <div class="header">
        <input type="hidden" :value="treffen" name="treffen" />
        <h3>
          <img :src="treffenLogo" alt="Treffen Logo" class="logo" />
          <b>
          {{ aktTreffen.find(t => t.uuid == treffen)?.event }}
          ({{ formatDate(aktTreffen.find(t => t.uuid == treffen)?.zeitraum_von) }} - {{ formatDate(aktTreffen.find(t => t.uuid == treffen)?.zeitraum_bis) }})
          </b>
        </h3>
    </div>
    <br />
    {{ info_message }}
  </div>

  <form v-else-if="!buchungZusammenfassung" @submit.prevent="handleSubmit">
    <div class="header">
        <input type="hidden" :value="treffen" name="treffen" />
          <img :src="treffenLogo" alt="Treffen Logo" class="logo" />
          <div class="header-text">
            <b>
            {{ aktTreffen.find(t => t.uuid == treffen)?.event }}
            ({{ formatDate(aktTreffen.find(t => t.uuid == treffen)?.zeitraum_von) }} - {{ formatDate(aktTreffen.find(t => t.uuid == treffen)?.zeitraum_bis) }})
            </b>
          </div>
    </div>
    <br />

    <div v-if="alleInfotexte.length > 0 && !isShopkasse">
        <fieldset v-for="(info, idx) in alleInfotexte" :key="idx">
        <legend>{{ info.index }}</legend>
          <span v-html="info.text"></span>
        </fieldset>
    </div>

    <h2>Persönliche Daten</h2>
    <div v-if="!isShopkasse" class="form-group">
      <label><strong>Club Mitglied bei</strong></label>
      <div  class="typ-choices-group">
        <div v-for="(label, value) in TYP_CHOICES" :key="value" class="form-check typ-choice-item">
          <input
            type="checkbox"
            class="form-check-input"
            :id="'gwcd-' + value"
            :value="value"
            v-model="personalie.gwcdmitglied"
            @change="onGwcdmitgliedChange(value)"
          />
          <label class="form-check-label" :for="'gwcd-' + value">{{ label }}</label>
        </div>
      </div>
    </div>
    <!-- <input v-if="personalie.gwcdmitglied === 'gwcd'" v-model="personalie.mitgliedsnummer" placeholder="Mitgliedsnummer *" required :disabled="!treffen" /> -->
    <input v-model="personalie.vorname" placeholder="Vorname *" required :disabled="!treffen" />
    <input v-model="personalie.nachname" placeholder="Nachname *" required :disabled="!treffen" />
    <input 
      v-model="personalie.geburtsdatum" 
      placeholder="Geburtsdatum *"
      required
      :disabled="!treffen" 
      @blur="validateGeburtsdatum(personalie.geburtsdatum, 'personalie')"
      />
    <input v-if="!personalie.gwcdmitglied.includes('tgo')"
      v-model="personalie.strasse" placeholder="Straße *" 
      :required="!personalie.gwcdmitglied.includes('tgo')" 
    :disabled="!treffen" 
    />
    <input v-if="!personalie.gwcdmitglied.includes('tgo')" 
    v-model="personalie.plz" placeholder="PLZ *" 
    :required="!personalie.gwcdmitglied.includes('tgo')" 
    :disabled="!treffen" 
    />
    <input v-if="!personalie.gwcdmitglied.includes('tgo')" 
    v-model="personalie.wohnort" placeholder="Ort *" 
    :required="!personalie.gwcdmitglied.includes('tgo')" 
    :disabled="!treffen" 
    />
    <input v-if="!personalie.gwcdmitglied.includes('tgo')" 
    v-model="personalie.telefon" placeholder="Telefon *" 
    :required="!personalie.gwcdmitglied.includes('tgo')" 
    :disabled="!treffen" 
    />
    <input v-if="!personalie.gwcdmitglied.includes('tgo')" 
    v-model="personalie.email" placeholder="E-Mail *" type="email" 
    :required="!personalie.gwcdmitglied.includes('tgo')" 
    :disabled="!treffen" 
    />
    <textarea v-if="!personalie.gwcdmitglied.includes('tgo')"
    v-model="personalie.stammtisch" placeholder="Dein Stammtisch/Club"></textarea>

    <div v-if="isBarbarossaWinger(personalie.stammtisch) && mitgliedGeprueft" class="form-group">
      <select data-testid="fahrer-tshirt-size" class="form-control" v-model="personalie.tshirt_size" required>
        <option v-for="(label, value) in TYP_TSHIRT_SIZE" :key="value" :value="value">{{ label }}</option>
      </select>
    </div>

    <h2 v-if="!personalie.gwcdmitglied.includes('tgo')">Notfall Daten</h2>
    <input v-if="!personalie.gwcdmitglied.includes('tgo')" 
    v-model="personalie.notfall" placeholder="Notfallkontakt Vor.- und Nachname"  :disabled="!treffen" />
    <input v-if="!personalie.gwcdmitglied.includes('tgo')" 
    v-model="personalie.nottelefon" placeholder="Notfallkontakt Telefon" :disabled="!treffen" />

    <h2 v-if="!personalie.gwcdmitglied.includes('tgo')">Fahrzeug Daten</h2>
    <div v-if="!personalie.gwcdmitglied.includes('tgo')" class="form-group">
      <select class="form-control" v-model="personalie.bikemodel" required>
        <option v-for="(label, value) in TYP_BIKE" :key="value" :value="value">{{ label }}</option>
      </select>
    </div>
    <div v-if="!personalie.gwcdmitglied.includes('tgo')" class="form-group">
      <select class="form-control" v-model="personalie.bikevariant" required>
        <option v-for="(label, value) in TYP_BIKE_VARIANT" :key="value" :value="value">{{ label }}</option>
      </select>
    </div>
    <input v-if="!personalie.gwcdmitglied.includes('tgo')" v-model="personalie.kennzeichen" placeholder="KFZ-Kennzeichen" :disabled="!treffen" />
    <input v-if="!personalie.gwcdmitglied.includes('tgo')" v-model="personalie.anreisekm" @input="personalie.anreisekm = personalie.anreisekm.replace(/\D/g, '')" placeholder="Anreise in Kilometer" :disabled="!treffen" />
    <textarea v-model="personalie.bemerkung" placeholder="Sonstiges"></textarea>

    <h2 v-if="!personalie.gwcdmitglied.includes('tgo')">Mitreisende</h2>
    <h2 v-if="!personalie.gwcdmitglied.includes('tgo')">weitere Personen</h2>
    <div v-if="mitreisende.length > 0">
      <ul style="margin-top:1em;margin-bottom: 2em;">
        <li v-for="(m, idx) in mitreisende" :key="idx" style="display:flex; align-items:center; gap:0.5em;">
          <span>
            {{ m.vorname }} {{ m.nachname }} 
            <!-- <span v-if="m.mitgliedsnummer">({{ m.mitgliedsnummer }})</span> -->
            <span v-if="m.geburtsdatum">, Geburtsdatum: {{ formatDate(m.geburtsdatum) }}</span>
            <span v-if="m.tshirt_size !== ''">, {{ TYP_TSHIRT_SIZE[m.tshirt_size] }}</span>
          </span>
          <button
            type="button"
            class="remove-btn"
            @click="removeMitreisender(idx)"
            title="Mitreisenden entfernen"
            style="margin-left:0.5em;"
            v-if="mitreisende.length > 1 && (m.vorname || m.nachname)"
          >🗑️</button>
        </li>
      </ul>
    </div>
    <div v-if="!personalie.gwcdmitglied.includes('tgo')" class="form-group">
      <!-- <input v-model="neuerMitreisender.mitgliedsnummer" placeholder="Mitgliedsnummer oder leer" :disabled="!treffen" /> -->
      <input v-model="neuerMitreisender.vorname" placeholder="Vorname"  :disabled="!treffen" />
      <input v-model="neuerMitreisender.nachname" placeholder="Nachname"  :disabled="!treffen" />
      <input 
        v-model="neuerMitreisender.geburtsdatum" 
        placeholder="Geburtsdatum *" 
        :disabled="!treffen" 
        @blur="validateGeburtsdatum(neuerMitreisender.geburtsdatum, 'mitreisender')"
      />
      <div v-if="isBarbarossaWinger(personalie.stammtisch) && mitreisenderGeprueft" class="form-group">
      <select data-testid="mitreisender-tshirt-size" class="form-control" v-model="neuerMitreisender.tshirt_size" required>
        <option v-for="(label, value) in TYP_TSHIRT_SIZE" :key="value" :value="value">{{ label }}</option>
      </select>
      </div>
      <button type="button" @click="addMitreisender" :disabled="!treffen">
        Mitreisenden hinzufügen
      </button>
    </div>    
    
    <h2 v-if="!isShopkasse">Übernachtung</h2>
    <div v-if="!isShopkasse" style="display: flex; gap: 1em; align-items: center;">
      <span 
        v-for="g in alleArtikelGruppen.slice().filter(g => g.gruppe == 'Camping' || g.gruppe == 'Hotel' || g.gruppe == 'Tagesgast').sort((a, b) => a.gruppe.localeCompare(b.gruppe))"
        :key="g.gruppe"
        style="display: flex; align-items: center;"
      >
        <label style="margin-right: 0.5em;">{{ g.gruppe }}</label>
        <input
          type="radio"
          :value="g.gruppe"
          v-model="ausgewaehlteArtikelgruppe"
          :disabled="!treffen"
          required
          name="artikelgruppe"
          style="margin-right: 1em;"
        />
      </span>
    </div>
    <div v-if="isShopkasse" style="display: flex; gap: 1em; align-items: center;">
      <span 
        v-for="g in alleArtikelGruppen.slice().filter(g => g.gruppe == g.gruppe == 'Tagesgast').sort((a, b) => a.gruppe.localeCompare(b.gruppe))"
        :key="g.gruppe"
        style="display: flex; align-items: center;"
      >
        <label style="margin-right: 0.5em;">{{ g.gruppe }}</label>
        <input
          type="radio"
          :value="g.gruppe"
          v-model="ausgewaehlteArtikelgruppe"
          :disabled="!treffen"
          required
          name="artikelgruppe"
          style="margin-right: 1em;"
        />
      </span>
    </div>

    <!-- Neue Felder für Anreise und Abreise, nur wenn Hotel oder Camping gewählt ist -->
    <div 
      v-if="ausgewaehlteArtikelgruppe === 'Tagesgast' || ausgewaehlteArtikelgruppe === 'Hotel' || ausgewaehlteArtikelgruppe === 'Camping'"
      style="display: flex; gap: 1em; align-items: center; margin-top: 1em;"
    >
      <label for="anreisetag">Anreisetag:</label>
      <input
        id="anreisetag"
        type="text"
        v-model="personalie.anreisetag"
        :disabled="!treffen"
        required
        @blur="validateGeburtsdatum(personalie.anreisetag, 'anreisetag')"
        style="max-width: 12em;"
      />
      <label for="abreisetag">Abreisetag:</label>
      <input
        id="abreisetag"
        type="text"
        v-model="personalie.abreisetag"
        :disabled="!treffen"
        required
        @blur="validateGeburtsdatum(personalie.abreisetag, 'abreisetag')"
        style="max-width: 12em;"
      />
    </div>

    <!-- T-Shirt Limit Anzeige -->
    <div v-if="aktuelleTShirtAnzahl >= 0 && isBarbarossaWinger(personalie.stammtisch)" style="margin-top: 1em; padding: 0.5em; background: #f0f8ff; border-left: 3px solid #007bff;">
      <strong>T-Shirt Übersicht:</strong> 
      {{ aktuelleTShirtAnzahl }} von {{ maxTShirtAnzahl }} kostenlosen T-Shirts bestellt
      <span v-if="tshirtLimitErreicht" style="color: #d32f2f;"> (Maximum erreicht. Weitere T-Shirts sind kostenpflichtig) </span>
    </div>

    <br />
    <table class="bestellung-tabelle">
      <thead>
        <tr>
          <th>Artikel</th>
          <th>Anzahl</th>
          <th>Artikelpreis</th>
          <th v-if="aktuellesTreffen && aktuellesTreffen.verl_zimmer">Optionanzahl</th>
          <th v-if="aktuellesTreffen && aktuellesTreffen.verl_zimmer">Optionpreis</th>
          <th v-if="aktuellesTreffen && aktuellesTreffen.buffet_pauschal">Buffet</th>
          <th v-if="aktuellesTreffen && aktuellesTreffen.buffet_pauschal">Buffet Anzahl</th>
          <th v-if="aktuellesTreffen && aktuellesTreffen.buffet_pauschal">Buffet Preis</th>
          <th>Summe €</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(b, idx) in bestellung" :key="idx">
          <td>
            <select 
                v-if="b.pauschal !== true"
                v-model="b.artikel" 
                required 
                :disabled="!treffen || !ausgewaehlteArtikelgruppe "
                >
              <option disabled value="">Bitte Artikel wählen *</option>
              <option v-for="a in gefilterteArtikel" :key="a.id" :value="a.id" :disabled="a.verfuegbar <= 0 || a.pauschal !== 'N' ">
                <!-- {{ a.artikelname }} {{ a.artikeldetail }} ({{ a.preis }}) -->
                {{ a.artikelname }} 
              </option>
            </select>
            <span v-else>
              {{ alleArtikel.find(a => a.id === b.artikel)?.artikelname || 'Pauschal-Artikel' }}
            </span>
          </td>
          <td>
            <input
              v-if="b.artikel && personalie.gwcdmitglied.includes('tgo') && !istTShirtArtikel(b.artikel)"
              type="number"
              min="1"
              max="99"
              v-model.number="b.anzahl"
              :disabled="!treffen"
              style="width: 60px; text-align: center;"
              @change="validateAnzahl(b)"
            />
            <span v-else-if="b.artikel && !istShopArtikel(b.artikel) && !istTShirtArtikel(b.artikel)">
              {{
                istUebernachtungsArtikel(b.artikel)
                  ? '1, ' + (b.anzahl || 1) +  (b.anzahl > 1 ? ' Nächte' : ' Nacht')
                  : (b.anzahl || 1)
              }}
            </span>
            <span v-else-if="b.artikel && istTShirtArtikel(b.artikel) && b.anzahl > 0 && b.option_anzahl > 0">
                  {{ b.anzahl }} {{ b.anzahl > 1 ? 'T-Shirts' : 'T-Shirt' }}
            </span>
            <input 
              v-else-if="b.artikel && istTShirtArtikel(b.artikel) && b.anzahl > 0 && b.option_anzahl === 0"
              type="number" 
              min="1" 
              max="10"
              v-model.number="b.anzahl" 
              :disabled="!treffen"
              style="width: 60px; text-align: center;"
              @change="validateAnzahl(b)"
            />
            <input 
              v-else-if="b.artikel && istShopArtikel(b.artikel)"
              type="number" 
              min="1" 
              max="99"
              v-model.number="b.anzahl" 
              :disabled="!treffen"
              style="width: 60px; text-align: center;"
              @change="validateAnzahl(b)"
            />
          </td>

          <td>
            <span v-if="b.artikel">
              {{
                artikelPreis(b.artikel, 1, personalie.stammtisch, idx)
              }}
            </span>
          </td>

          <td>
            <span v-if="b.artikel != '' && b.option?.option?.startsWith('T') && b.option_anzahl > 0">
              {{ b.option_anzahl > 0 ?  b.option_anzahl  : '' }}
            </span>

            <span v-if="b.artikel != '' && b.option?.option?.startsWith('E') && b.option_anzahl > 0">
              {{ b.option_anzahl > 0 ?  b.option_anzahl + ' ' + (b.option_anzahl > 1 ? 'Personen' : 'Person') : '' }}
            </span>

            <span v-if="b.artikel != '' && (b.option?.option?.startsWith('F') || b.option?.option?.startsWith('S')) && b.option_anzahl > 0">
              {{ b.option_anzahl > 0 ?  b.option_anzahl + ' ' + (b.option_anzahl > 1 ? 'Nächte' : 'Nacht') : '' }}
            </span>
          </td>

          <td>
            <span v-if="b.artikel != '' && b.option?.option?.startsWith('T') && b.option_anzahl > 0">
               {{ b.option.option_text }} ({{ b.option.option_preis }})
            </span>

            <span v-if="b.artikel != '' && b.option?.option?.startsWith('E') && b.option_anzahl > 0">
               {{ b.option.option_text }} ({{ b.option.option_preis }})
            </span>

            <span v-if="b.artikel != '' && (b.option?.option?.startsWith('F') || b.option?.option?.startsWith('S')) && b.option_anzahl > 0">
            <select
              v-if="b.artikel && b.option_anzahl > 0 && alleArtikel.find(a => a.id === b.artikel)?.optionen?.length"
              v-model="b.option"
              :disabled="!treffen || b.option_anzahl === 0"
            >
              <option disabled value="">Bitte Option wählen *</option>
              <option
                v-for="opt in alleArtikel.find(a => a.id === b.artikel).optionen.filter(o => o.option && (o.option.startsWith('F') || o.option.startsWith('S')  ))"
                :key="opt.option"
                :value="opt"
              >
                {{ opt.option_text }} ({{ opt.option_preis }})
              </option>
            </select>          
            </span>
          </td>
          
          <td v-if="aktuellesTreffen && aktuellesTreffen.buffet_pauschal">
            <span v-if="b.artikel"> 
              {{ b.buffet_text }}
            </span>
          </td>

          <td v-if="aktuellesTreffen && aktuellesTreffen.buffet_pauschal">
            <span v-if="b.artikel"> 
              {{ b.buffet_anzahl > 0 ? b.buffet_anzahl : '' }}
            </span>
          </td>

          <td v-if="aktuellesTreffen && aktuellesTreffen.buffet_pauschal">
            <span v-if="b.artikel"> 
              {{ b.buffet_anzahl > 0 ? b.buffet_preis : '' }}
            </span>
          </td>

          <td>
            <span v-if="b.artikel">
              {{
                (
                  artikelPreis(b.artikel, b.anzahl || 1, personalie.stammtisch, idx) +
                  (
                    alleArtikel.find(a => a.id === b.artikel)?.optionen?.find(opt => opt.option === b.option.option)?.option_preis || 0
                  ) * (b.option_anzahl || 0) +
                  ((b.buffet_preis || 0) * (b.buffet_anzahl || 0))
                ).toFixed(2)
              }}
            </span>          
          </td>
          <td>
            <button
              type="button"
              @click="removeBestellung(idx)"
              v-if="bestellung.length > 1 && !b.pauschal && (b.artikel || b.anzahl)"
              class="remove-btn"
              title="Artikel löschen"
            >🗑️</button>
          </td>          
        </tr>
      </tbody>
    </table>

    <button 
      id="addBestellung" 
      type="button"
      @click="addBestellung" 
      :disabled="!treffen">
      weiteren Artikel hinzufügen
    </button>

    
    <!-- Summe anzeigen -->
    <div style="margin-top:1em; font-weight:bold;">
      Gesamt Summe: {{ bestellSumme.toFixed(2) }} €
    </div>    


    <h2>Zur Kenntnisnahme und Bestätigung</h2>
    <div v-for="rt in alleRechtstexte" :key="rt.id">
      <label>
        <input type="checkbox" class="big-checkbox" :value="rt.id" v-model="rechtstexte" />
        <span class="rechtstext-label" v-html="rt.text"></span>
      </label>
    </div>
    <button type="submit" :disabled="isSubmitting || !alleRechtstexteBestaetigt">Anmelden und Buchen</button>
    <br />
  </form>

  <div v-else class="zusammenfassung">
    <!-- Django Template Buchungszusammenfassung (Priorität) -->
    <div v-if="buchungHtml" v-html="buchungHtml"></div>
    
    <!-- Fallback: Vue Buchungszusammenfassung -->
    <div v-else-if="buchungZusammenfassung">
      <h2 style="font-weight:bold;">Buchungszusammenfassung</h2>

    <div style="margin-left:2em; padding:0.5em 1em; border-left:3px solid #ccc; background:#f9f9f9;">
      <table  cellspacing="2" cellpading="4">
        <tbody>
          <tr>
          <td>Treffen:</td> <td>{{ buchungZusammenfassung.treffen.event }} ({{ formatDate(buchungZusammenfassung.treffen.zeitraum_von) }} - {{ formatDate(buchungZusammenfassung.treffen.zeitraum_bis) }})</td>
          </tr>
          <tr>
          <td>Mitgliedsnummer:</td> <td>{{ buchungZusammenfassung.personalie.mitgliedsnummer }}</td>
          </tr>
          <tr>
          <td>Name:</td> <td>{{ buchungZusammenfassung.personalie.vorname }} {{ buchungZusammenfassung.personalie.nachname }}</td>
          </tr>
          <tr>
          <td>Geburtsdatum:</td> <td>{{ formatDate(buchungZusammenfassung.personalie.geburtsdatum) }}</td >
          </tr>
          <tr>
          <td>Adresse:</td> <td>{{ buchungZusammenfassung.personalie.strasse }}</td>
          </tr>
          <tr>
          <td>Wohnort:</td> <td>{{ buchungZusammenfassung.personalie.plz }} {{ buchungZusammenfassung.personalie.wohnort }}</td>
          </tr>
          <tr>
          <td>Telefon:</td> <td>{{ buchungZusammenfassung.personalie.telefon }}</td>
          </tr>
          <tr>
          <td>E-Mail:</td> <td>{{ buchungZusammenfassung.personalie.email }}</td>
          </tr>
        </tbody>
      </table>
      <p>{{ buchungZusammenfassung.personalie.bemerkung || "  Keine Bemerkung" }}</p>
    </div>    

    <div v-if="buchungZusammenfassung.mitreisende.length" style="margin-left:2em; padding:0.5em 1em; border-left:3px solid #ccc; background:#f9f9f9;">
      <strong>Mitreisende:</strong>
      <ul>
        <li v-for="(m, idx) in buchungZusammenfassung.mitreisende" :key="idx">
          {{ m.vorname }} {{ m.nachname }} , Geb.datum: {{ formatDate(m.geburtsdatum) }} ({{ m.mitgliedsnummer }})
        </li>
      </ul>
    </div>

    <div style="margin-left:2em; padding:0.5em 1em; border-left:3px solid #ccc; background:#f9f9f9;">
      <strong>Bestellungen :</strong>
      <p>Reservierungsanfrage für den Zeitraum vom {{ buchungZusammenfassung.personalie.anreisetag }} bis {{ buchungZusammenfassung.personalie.abreisetag }}</p>
      <table style="border-collapse:collapse; margin-left:2em; background:#f9f9f9;">
        <thead>
          <tr>
            <th style="border:1px solid #ccc; padding:0.3em 0.7em;">Artikel</th>
            <th style="border:1px solid #ccc; padding:0.3em 0.7em;">Option</th>
            <th style="border:1px solid #ccc; padding:0.3em 0.7em;">Einzelpreis</th>
            <th style="border:1px solid #ccc; padding:0.3em 0.7em;">Anzahl</th>
            <th style="border:1px solid #ccc; padding:0.3em 0.7em;">Gesamt</th>
          </tr>
        </thead>
        <tbody v-for="(b, idx) in buchungZusammenfassung.bestellung" :key="idx">
          <tr v-if="b.artikelId">
            <td style="border:1px solid #ccc; padding:0.3em 0.7em;">{{ b.artikelname }} ({{ b.artikeldetail }})</td>
            <td style="border:1px solid #ccc; padding:0.3em 0.7em;"></td>
            <td style="border:1px solid #ccc; padding:0.3em 0.7em; text-align: right;">{{ b.preis }}</td>
            <td style="border:1px solid #ccc; padding:0.3em 0.7em;">
              {{
                  istUebernachtungsArtikel(b.artikelId)
                    ? '1, ' + (b.anzahl || 1) +  (b.anzahl > 1 ? ' Nächte' : ' Nacht')
                  : (b.anzahl || 1) + ' Stück, '
              }}
            </td>
            <td style="border:1px solid #ccc; padding:0.3em 0.7em; text-align: right;">
              {{
                  (b.anzahl * parseFloat(b.preis)).toFixed(2)
              }} 
            </td>
          </tr>
          <template v-if="b.option_array && b.option_array.length">
            <tr v-for="(opt, oidx) in b.option_array" :key="oidx">
              <td style="border:1px solid #ccc; padding:0.3em 0.7em;"></td>

              <td style="border:1px solid #ccc; padding:0.3em 0.7em;" v-if="opt.value.startsWith('F')">{{ opt.text }} </td>                  
              <td style="border:1px solid #ccc; padding:0.3em 0.7em; text-align: right;" v-if="opt.value.startsWith('F')">{{ opt.preis }}</td>
              <td style="border:1px solid #ccc; padding:0.3em 0.7em;" v-if="opt.value.startsWith('F')">{{ opt.anzahl > 0 ? ' + ' + opt.anzahl + ' ' + (opt.anzahl > 1 ? 'Nächte' : 'Nacht') : '' }}</td>
              <td style="border:1px solid #ccc; padding:0.3em 0.7em; text-align: right;" v-if="opt.value.startsWith('F')">
                {{
                    (opt.anzahl * parseFloat(opt.preis)).toFixed(2)
                }} 
              </td>

              <td style="border:1px solid #ccc; padding:0.3em 0.7em;" v-if="opt.value.startsWith('S')">{{ opt.text }}</td>
              <td style="border:1px solid #ccc; padding:0.3em 0.7em; text-align: right;" v-if="opt.value.startsWith('S')">{{ opt.preis }}</td>
              <td style="border:1px solid #ccc; padding:0.3em 0.7em;" v-if="opt.value.startsWith('S')">{{ opt.anzahl > 0 ? ' + ' + opt.anzahl + ' ' + (opt.anzahl > 1 ? 'Nächte' : 'Nacht') : '' }}</td>
              <td style="border:1px solid #ccc; padding:0.3em 0.7em; text-align: right;" v-if="opt.value.startsWith('S')">
                {{
                    (opt.anzahl *parseFloat(opt.preis)).toFixed(2)
                }} 
              </td>

              <td style="border:1px solid #ccc; padding:0.3em 0.7em;" v-if="opt.value.startsWith('B1')">{{ opt.text }}</td>
              <td style="border:1px solid #ccc; padding:0.3em 0.7em; text-align: right;" v-if="opt.value.startsWith('B1')">{{ opt.preis }}</td>
              <td style="border:1px solid #ccc; padding:0.3em 0.7em;" v-if="opt.value.startsWith('B1')"></td>
              <td style="border:1px solid #ccc; padding:0.3em 0.7em; text-align: right;" v-if="opt.value.startsWith('B1')">
                {{
                    (parseFloat(opt.preis)).toFixed(2)
                }} 
              </td>
              
              <td style="border:1px solid #ccc; padding:0.3em 0.7em;" v-if="opt.value.startsWith('B2')">{{ opt.text }} </td>
              <td style="border:1px solid #ccc; padding:0.3em 0.7em; text-align: right;" v-if="opt.value.startsWith('B2')">{{ opt.preis }}</td>
              <td style="border:1px solid #ccc; padding:0.3em 0.7em;" v-if="opt.value.startsWith('B2')">{{ opt.anzahl > 0 ? ' + ' + opt.anzahl + ' ' + (opt.anzahl > 1 ? 'Nächte' : 'Nacht') : '' }}</td>
              <td style="border:1px solid #ccc; padding:0.3em 0.7em; text-align: right;" v-if="opt.value.startsWith('B2')">
                {{
                    (opt.anzahl * parseFloat(opt.preis)).toFixed(2)
                }} 
              </td>

            </tr>
          </template>
        </tbody>
      </table>
      <div style="font-weight:bold;">
        Gesamtsumme: {{
          buchungZusammenfassung.bestellung.reduce((sum, b) =>
            sum +
            (b.anzahl * parseFloat(b.preis)) +
            b.option_array.reduce((osum, opt) => osum + (parseFloat(opt.preis) * (opt.anzahl || 0)), 0)
          , 0).toFixed(2)
        }} €
      </div>
    </div>
    <div>
      <strong>Kenntnissnahme und Bestätigung:</strong>
      <ul>
        <li v-for="rt in buchungZusammenfassung.rechtstexte" :key="rt.id">
          <span v-html="rt.text"></span>
        </li>
      </ul>
    </div>
    <br />
    <strong v-if="!personalie.gwcdmitglied.includes('tgo')">
      Eine Email zur Bestätigung wird an die angegebene Mailadresse gesendet.
    </strong>
    <br />
    </div> <!-- Ende Vue Fallback Zusammenfassung -->
  </div>
</template>

<style scoped>
body {
  font-family: Arial, sans-serif;
  background: #f5f5f5;
  color: #333;
  font-size: 12px;
}
.header {
  display: flex;
  align-items: center;
  gap: 1.5em;
}

.header-text {
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-size: 1.1em;
  color: #222 !important;   /* oder #000 für echtes Schwarz */
  background: transparent !important;
  -webkit-text-fill-color: #222 !important; /* für Safari/Firefox */
}

form, .zusammenfassung {
  max-width: 100vw;
  margin: 0 auto;
  padding: 1em;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

h2 {
  font-size: 1.1em;
  margin-top: 1em;
}

input, select, textarea, button {
  width: 100%;
  font-size: 1em;
  padding: 0.7em;
  border-radius: 4px;
  border: 1px solid #ccc;
  box-sizing: border-box;
}

.logo {
  height: 75px;
  width: 66px;
}
span b, span strong {
  font-weight: bold !important;
}

button {
  width: auto;
  padding: 0.7em 1.5em;
  background: #1976d2;
  color: #fff;
  border: none;
  margin-top: 0.5em;
  border-radius: 4px;
  font-weight: bold;
}

button[disabled] {
  background: #aaa;
}

div[role="group"], .bestellung-row, .mitreisende-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5em;
}

label {
  display: flex;
  align-items: flex-start;
  gap: 0.5em;
  margin-bottom: 0.5em;
}
.rechtstext-label {
  line-height: 1.3;
}
.big-checkbox[type="checkbox"] {
  width: 1rem;
  height: 1rem;
  margin-top: 0.1rem;
  accent-color: #007bff; /* Optional: Bootstrap-Blau */
  display: block;
}
b, strong {
  font-weight: bold !important;
}


.remove-btn {
  background: none;
  border: none;
  color: #b71c1c;
  font-size: 1.3em;
  padding: 0 0.5em;
  cursor: pointer;
  margin-left: 0.5em;
  line-height: 1;
  transition: color 0.2s;
}
.remove-btn:hover {
  color: #f44336;
}

.rechtstext-label,
fieldset,
fieldset legend,
span[v-html] {
  font-size: 1em;
  color: #222 !important;   /* oder #000 für echtes Schwarz */
  background: transparent !important;
  -webkit-text-fill-color: #222 !important; /* für Safari/Firefox */
}

.alert {
  padding: 1em;
  background-color: #fff3cd;
  border: 1px solid #ffeeba;
  color: #856404;
  border-radius: 4px;
  margin-bottom: 1em;
}

.bestellung-tabelle {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1em;
}

.bestellung-tabelle th, .bestellung-tabelle td {
  border: 1px solid #ccc;
  padding: 0.5em;
  text-align: left;
  font-size: 0.9em;
}

.bestellung-tabelle th {
  background: #f5f5f5;
  font-weight: bold;
}

.bestellung-tabelle tr:nth-child(even) {
  background: #fafafa;
}
/* Handy: bis 600px */
@media (max-width: 600px) {
  .typ-choices-group {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0.3em 0.6em;
  }
  .typ-choice-item {
    flex-direction: column-reverse;
    align-items: center;
    gap: 0.1em;
  }
  .typ-choices-group .form-check-label {
    font-size: 0.72em;
    line-height: 1.2;
    text-align: center;
  }
  .rechtstext-label,
  span[v-html] {
    font-size: 1em;
  }
  form, .zusammenfassung {
    padding: 0.5em;
    max-width: 98vw;
    border-radius: 2px;
  }
  input, select, textarea, button {
    font-size: 0.95em;
    padding: 0.5em;
  }
  h2 {
    font-size: 1em;
  }
  .header-text {
    font-size: 1.3em;
  }
}

.typ-choices-group {
  display: flex;
  gap: 1.5em;
  flex-wrap: wrap;
  margin-top: 0.4em;
  margin-bottom: 0.6em;
}

.typ-choice-item {
  display: flex;
  align-items: center;
  gap: 0.4em;
}

/* Handy quer: Landscape-Phones (Höhe ≤ 500px, Querformat) */
@media (orientation: landscape) and (max-height: 500px) {
  .typ-choices-group {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0.3em 0.6em;
  }
  .typ-choice-item {
    flex-direction: column-reverse;
    align-items: center;
    gap: 0.1em;
  }
  .typ-choices-group .form-check-label {
    font-size: 0.72em;
    line-height: 1.2;
    text-align: center;
  }
}

/* Tablet: 601px bis 1023px */
@media (min-width: 601px) and (max-width: 1023px) {
  .rechtstext-label,
  span[v-html] {
    font-size: 1.05em;
  }
  form, .zusammenfassung {
    max-width: 90vw;
    padding: 1.2em;
    border-radius: 6px;
  }
  input, select, textarea, button {
    font-size: 1em;
    padding: 0.7em;
  }
  h2 {
    font-size: 1.15em;
  }
  .header-text {
    font-size: 1.35em;
  }
}

/* PC: ab 1024px */
@media (min-width: 1024px) {
  .rechtstext-label,
  span[v-html] {
    font-size: 1.1em;
  }
  form, .zusammenfassung {
    max-width: 900px;
    padding: 2em;
    border-radius: 8px;
  }
  input, select, textarea, button {
    font-size: 1.1em;
    padding: 1em;
  }
  h2 {
    font-size: 1.2em;
  }
  .header-text {
    font-size: 1.4em;
  }
}

</style>
